/** @shell standard @header bar */
import React, { useCallback, useEffect, memo, useState, useMemo } from 'react';
import { View, Text, StyleSheet, Alert, Platform } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withDelay,
  useReducedMotion,
  Easing,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { SvgIcon } from '../components/common/SvgIcon';
import { MainLoader } from '../components/load-main/MainLoader';
import { Clickable } from '../components/common/Clickable';
import { ScreenHeader, useScreenHeaderScroll } from '../components/common/ScreenHeader';
import { ContentCard } from '../components/common/ContentCard';
import { useNavigation } from '@react-navigation/native';
import { appTheme, useThemeColors, type ThemeColors } from '../shared/theme';
import { useAddresses, useDeleteAddress, useSetDefaultAddress } from '../data/domains/addresses';
import { useAuthStore } from '../shared/auth/store';
import { useRequireVerified } from '../shared/auth/useRequireVerified';
import { useAuthNavigation, useExitOrGoBack } from '../shared/navigation/useAuthNavigation';
import { useTranslation, getLocale, formatNumber } from '../shared/i18n';
import { getAreaBilingualLabel } from '../data/domains/addresses';
import { useQueryClient } from '@tanstack/react-query';
import { useSubscriptionContext } from '../data/domains/subscription';
import { runInvalidationForMutation } from '../data/query/runInvalidation';
import { getAddressDeliveryAssignmentMode } from '../data/domains/config';
import { buildAddressDeliverySummaryLabels, getAllParticularDeliveryDates } from '../data/domains/addresses';
import { AddressDeliveryDatesModal } from './components/AddressDeliveryDatesModal';
import { AddressDeliveryAssignmentSheet } from './components/AddressDeliveryAssignmentSheet';
import { createPageFooterOutlineCtaStyles } from '../shared/theme/styles/buttons';
import { ErrorState } from '../components/common/ErrorState';
import { androidHeaderTopInset, androidAppHeaderContentHeight } from '../shared/theme/sharedStyles';
import moment from 'moment';
import {
  StackScreenShell,
  STACK_SCROLL_GUTTER,
  STACK_SECTION_GAP,
  androidScaleFont,
  androidScaleSize,
  androidFlatSurfaceStyle,
  studioCardShell,
  DASH_STUDIO_RADIUS,
} from '../shared/theme/studio/stackScreenShell';
import {
  cappedListEnterDelay,
  LIST_ENTER_DURATION_MS,
  LIST_ENTER_TRANSLATE_Y,
} from '../shared/theme/studio/studioMotion';

const HEADER_HEIGHT = androidAppHeaderContentHeight();
const FOOTER_VERTICAL_PAD = appTheme.spacing.md;
const FOOTER_CTA_HEIGHT = androidScaleSize(52);

function footerTotalHeight(bottom_inset: number): number {
  return FOOTER_VERTICAL_PAD * 2 + FOOTER_CTA_HEIGHT + bottom_inset;
}

const ENTER_EASING = Easing.out(Easing.cubic);

interface AddressCardProps {
  address: any;
  index: number;
  onEdit: () => void;
  onDelete: () => void;
  onSetDefault: () => void;
  onSetDeliveryDates?: () => void;
  showDeliveryDates?: boolean;
  deliverySummary?: string | null;
  deliveryCtaLabel?: string;
  styles: ReturnType<typeof createAddressListStyles>;
  themeColors: ThemeColors;
}

const AddressCard = memo(({ address, index, onEdit, onDelete, onSetDefault, onSetDeliveryDates, showDeliveryDates, deliverySummary, deliveryCtaLabel, styles, themeColors }: AddressCardProps) => {
  const { t } = useTranslation();
  const reduce_motion = useReducedMotion();
  const enterOpacity = useSharedValue(reduce_motion ? 1 : 0);
  const enterTranslateY = useSharedValue(reduce_motion ? 0 : LIST_ENTER_TRANSLATE_Y);

  useEffect(() => {
    if (reduce_motion) {
      enterOpacity.value = 1;
      enterTranslateY.value = 0;
      return;
    }
    enterOpacity.value = withTiming(1, { duration: LIST_ENTER_DURATION_MS, easing: ENTER_EASING });
    enterTranslateY.value = withDelay(
      cappedListEnterDelay(index),
      withTiming(0, { duration: LIST_ENTER_DURATION_MS, easing: ENTER_EASING }),
    );
  }, [index, reduce_motion, enterOpacity, enterTranslateY]);

  const cardStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: enterTranslateY.value }],
    opacity: enterOpacity.value,
  }));

  const area_label =
    address.area_name ??
    (address.area && typeof address.area === 'object'
      ? getAreaBilingualLabel(address.area as Record<string, unknown>)
      : address.area) ??
    '';
  const lines: string[] = [];
  if (area_label || address.block) {
    lines.push([area_label, address.block ? `${t('address.block')} ${address.block}` : ''].filter(Boolean).join(', '));
  }
  if (address.street || address.house_name) {
    lines.push([address.street ? `${t('address.street')} ${address.street}` : '', address.house_name ? `${t('address.house')} ${address.house_name}` : ''].filter(Boolean).join(', '));
  }
  if (address.floor || address.flat) {
    lines.push([address.floor ? `${t('address.floor')} ${address.floor}` : '', address.flat ? `${t('address.flat')} ${address.flat}` : ''].filter(Boolean).join(', '));
  }
  if (address.landmark) {
    lines.push(`${t('address.landmark')}: ${address.landmark}`);
  }
  if (lines.length === 0) {
    lines.push('—');
  }

  return (
    <Animated.View style={cardStyle}>
      <ContentCard
        variant="surface"
        borderless
        padding="lg"
        style={[
          styles.addressCardWrap,
          studioCardShell(themeColors),
          { borderRadius: DASH_STUDIO_RADIUS, backgroundColor: themeColors.surface },
        ]}>
        <View style={styles.cardHeader}>
          <View style={styles.titleRow}>
            <View style={styles.iconWrap}>
              <SvgIcon name="map" size={22} color={themeColors.primary} />
            </View>
            <Text style={styles.addressName} numberOfLines={1}>
              {address.name || t('address.addressFallback')}
            </Text>
          </View>
          <View style={styles.headerActions}>
            <Clickable onPress={onEdit} accessibilityLabel={t('address.editAddress')} accessibilityRole="button">
              <View style={styles.cardActionBtn}>
                <View style={styles.cardActionBtnInner}>
                  <SvgIcon name="edit" size={18} color={themeColors.primary} />
                  <Text style={[styles.cardActionBtnText, { color: themeColors.primary }]}>{t('address.edit')}</Text>
                </View>
              </View>
            </Clickable>
            <Clickable onPress={onDelete} accessibilityLabel={t('address.deleteAddress')} accessibilityRole="button">
              <View style={styles.cardActionBtn}>
                <View style={styles.cardActionBtnInner}>
                  <SvgIcon name="trash" size={18} color={themeColors.error} />
                  <Text style={[styles.cardActionBtnText, { color: themeColors.error }]}>{t('address.delete')}</Text>
                </View>
              </View>
            </Clickable>
          </View>
        </View>

        <View style={styles.details}>
          {address.is_default ? (
            <View style={styles.defaultBadge}>
              <Text style={styles.defaultBadgeText}>{t('address.default')}</Text>
            </View>
          ) : null}
          {showDeliveryDates && deliverySummary ? (
            <Text style={styles.deliverySummaryText} numberOfLines={2}>
              {deliverySummary}
            </Text>
          ) : null}
          {lines.map((line, i) => (
            <Text key={i} style={styles.detailLine}>
              {line}
            </Text>
          ))}
        </View>

        <View style={styles.footer}>
          <View style={styles.phoneRow}>
            <SvgIcon name="call" size={16} color={themeColors.textSecondary} />
            <Text style={styles.phoneText}>{address.phone || '—'}</Text>
          </View>
          <View style={styles.footerActions}>
            {showDeliveryDates && onSetDeliveryDates ? (
              <Clickable onPress={onSetDeliveryDates} accessibilityLabel={deliveryCtaLabel ?? t('address.setDeliveryDates')} accessibilityRole="button">
                <View style={styles.cardFooterBtnWrap}>
                  <View style={[styles.cardFooterBtnInner, styles.cardFooterBtnInnerTertiary]}>
                    <Text style={[styles.cardFooterBtnText, styles.cardFooterBtnTextTertiary]} numberOfLines={1}>
                      {deliveryCtaLabel ?? t('address.setDeliveryDates')}
                    </Text>
                  </View>
                </View>
              </Clickable>
            ) : null}
            {!address.is_default && (
              <Clickable onPress={onSetDefault} accessibilityLabel={t('address.setDefault')} accessibilityRole="button">
                <View style={styles.cardFooterBtnWrap}>
                  <View style={[styles.cardFooterBtnInner, styles.cardFooterBtnInnerSuccess]}>
                    <Text style={[styles.cardFooterBtnText, styles.cardFooterBtnTextSuccess]} numberOfLines={1}>
                      {t('address.setDefault')}
                    </Text>
                  </View>
                </View>
              </Clickable>
            )}
          </View>
        </View>
      </ContentCard>
    </Animated.View>
  );
});

function createAddressListStyles(c: ThemeColors) {
  const sf = androidScaleFont;
  const ss = androidScaleSize;
  return StyleSheet.create({
    container: { flex: 1 },
    center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
    scroll: { flex: 1 },
    scrollContent: { paddingHorizontal: STACK_SCROLL_GUTTER, paddingTop: appTheme.spacing.md },

    addressCardWrap: {
      marginBottom: STACK_SECTION_GAP,
      overflow: 'visible',
    },
    cardHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: appTheme.spacing.md,
    },
    titleRow: { flexDirection: 'row', alignItems: 'center', flex: 1, gap: appTheme.spacing.sm, minWidth: 0 },
    iconWrap: {
      width: ss(40),
      height: ss(40),
      borderRadius: ss(appTheme.borderRadius.md),
      backgroundColor: c.primaryUltra,
      borderWidth: 1,
      borderColor: c.primaryFade,
      justifyContent: 'center',
      alignItems: 'center',
      ...androidFlatSurfaceStyle(),
    },
    addressName: {
      ...appTheme.typography.body,
      fontWeight: '600',
      color: c.text,
      flex: 1,
    },
    defaultBadge: {
      backgroundColor: c.primary,
      paddingHorizontal: appTheme.spacing.sm,
      paddingVertical: appTheme.spacing.xs,
      borderRadius: appTheme.borderRadius.lg,
      textAlign: 'center',
      alignSelf: 'flex-start',
      marginVertical: 4,
    },
    defaultBadgeText: {
      ...appTheme.typography.captionSmall,
      color: c.onPrimary,
    },
    headerActions: { flexDirection: 'row', gap: appTheme.spacing.md, alignItems: 'center' },
    cardActionBtn: {},
    cardActionBtnInner: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      paddingHorizontal: appTheme.spacing.xs,
      paddingVertical: appTheme.spacing.xs,
      backgroundColor: c.surface,
    },
    cardActionBtnText: {
      ...appTheme.typography.bodySmall,
      fontWeight: '600',
    },

    details: { marginBottom: appTheme.spacing.md },
    detailLine: {
      ...appTheme.typography.bodySmall,
      color: c.textSecondary,
      marginBottom: appTheme.spacing.xs,
      lineHeight: sf(20),
    },
    deliverySummaryText: {
      ...appTheme.typography.caption,
      color: c.primary,
      fontWeight: '600',
      marginBottom: appTheme.spacing.xs,
    },

    footer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingTop: appTheme.spacing.md,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: c.borderLight,
    },
    phoneRow: { flexDirection: 'row', alignItems: 'center', gap: appTheme.spacing.sm },
    phoneText: {
      ...appTheme.typography.bodySmall,
      color: c.textSecondary,
      fontWeight: '500',
    },
    footerActions: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: appTheme.spacing.sm,
      flexShrink: 0,
      flexWrap: 'wrap',
    },
    cardFooterBtnWrap: {
      flexShrink: 0,
      backgroundColor: c.surface,
      borderRadius: 999,
      ...Platform.select({
        ios: { ...appTheme.shadows.md, shadowColor: c.shadowColor },
        android: androidFlatSurfaceStyle(),
      }),
    },
    cardFooterBtnInner: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: ss(7),
      paddingHorizontal: ss(12),
      borderRadius: 999,
      borderWidth: 1,
    },
    cardFooterBtnInnerTertiary: {
      backgroundColor: c.tertiaryUltra,
      borderColor: c.tertiaryFade,
    },
    cardFooterBtnInnerSuccess: {
      backgroundColor: c.successUltra,
      borderColor: c.successFade,
    },
    cardFooterBtnText: {
      fontSize: sf(10),
      fontWeight: '800',
      letterSpacing: 0.35,
      textAlign: 'center',
    },
    cardFooterBtnTextTertiary: {
      color: c.tertiary,
    },
    cardFooterBtnTextSuccess: {
      color: c.success,
    },

    emptyState: { alignItems: 'center', paddingVertical: 48 },
    emptyIconWrap: {
      width: 80,
      height: 80,
      borderRadius: 40,
      backgroundColor: c.primaryUltra,
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: appTheme.spacing.lg,
    },
    emptyTitle: {
      ...appTheme.typography.subtitle,
      color: c.text,
      marginBottom: appTheme.spacing.xs,
    },
    emptySubtitle: {
      ...appTheme.typography.bodySmall,
      color: c.textSecondary,
      marginBottom: appTheme.spacing.lg,
    },

    pageFooter: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: FOOTER_VERTICAL_PAD,
      paddingHorizontal: STACK_SCROLL_GUTTER,
      backgroundColor: c.surface,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: c.borderLight,
      ...Platform.select({
        ios: { ...appTheme.shadows.lg, shadowColor: c.shadowColor },
        android: androidFlatSurfaceStyle(),
      }),
    },
    pageFooterCtaWrap: {
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
    },

    retryBtnWrap: {},
    retryBtnInner: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      height: 50,
      paddingHorizontal: 22,
      borderRadius: 14,
      backgroundColor: c.primary,
      ...appTheme.shadows.sm,
      shadowColor: c.shadowColor,
    },
    retryBtnText: {
      ...appTheme.typography.bodyMedium,
      fontWeight: '600',
      color: c.onPrimary,
    },

    headerRefreshWrap: { minWidth: 44, minHeight: 38 },
    headerRefreshInner: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 44,
      minHeight: 38,
      borderRadius: 10,
    },

    errorWrap: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: appTheme.spacing.xl },
    errorText: {
      ...appTheme.typography.body,
      color: c.textSecondary,
      marginBottom: appTheme.spacing.lg,
    },
  });
}

export const AddressListScreen: React.FC = () => {
  const navigation = useAuthNavigation();
  const on_back_exit = useExitOrGoBack();
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();
  const themeColors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createAddressListStyles(themeColors), [themeColors]);
  const { scrollY, onScroll } = useScreenHeaderScroll();
  const outline_footer_cta = useMemo(() => createPageFooterOutlineCtaStyles(themeColors), [themeColors]);
  const user = useAuthStore(s => s.user);
  useRequireVerified('AddressLists');
  const query_client = useQueryClient();
  const { data: addressesData, isLoading, error, refetch } = useAddresses();
  const deleteMutation = useDeleteAddress();
  const setDefaultMutation = useSetDefaultAddress();
  const { activeSubscriptions, availableDates, getAddressDates } = useSubscriptionContext();
  const [deliveryDatesAddress, setDeliveryDatesAddress] = useState<{ id: number; name?: string } | null>(null);
  const assignment_mode = getAddressDeliveryAssignmentMode();
  const use_redesigned_delivery_ux = assignment_mode !== 'disabled';
  const locale = getLocale();

  const all_particular_delivery_dates = useMemo(
    () => getAllParticularDeliveryDates(Array.isArray(activeSubscriptions) ? activeSubscriptions : []),
    [activeSubscriptions],
  );

  const addresses = Array.isArray(addressesData) ? addressesData : [];
  const has_active_subscriptions = activeSubscriptions.length > 0;
  const show_delivery_assignment = has_active_subscriptions && addresses.length > 1;

  const upcoming_delivery_count_by_address = useMemo(() => {
    const today = moment().startOf('day');
    const counts = new Map<number, number>();
    for (const address of addresses) {
      const dates = getAddressDates(address.id);
      const upcoming = dates.filter(d => {
        const m = moment(d, 'YYYY-MM-DD', true);
        return m.isValid() && m.isSameOrAfter(today, 'day');
      });
      counts.set(address.id, upcoming.length);
    }
    return counts;
  }, [addresses, getAddressDates]);

  const most_delivery_address_id = useMemo(() => {
    let best_id: number | null = null;
    let best_count = -1;
    for (const address of addresses) {
      const count = upcoming_delivery_count_by_address.get(address.id) ?? 0;
      if (count > best_count) {
        best_count = count;
        best_id = address.id;
        continue;
      }
      if (count === best_count && best_id != null) {
        const best_addr = addresses.find(a => a.id === best_id);
        if (address.is_default && !best_addr?.is_default) {
          best_id = address.id;
        } else if (address.is_default === best_addr?.is_default && address.id > best_id) {
          best_id = address.id;
        }
      }
    }
    return best_count > 0 ? best_id : null;
  }, [addresses, upcoming_delivery_count_by_address]);

  const formatDeliverySummary = useCallback(
    (addressId: number) => {
      const count = upcoming_delivery_count_by_address.get(addressId) ?? 0;
      if (count <= 0) {
        return null;
      }
      if (most_delivery_address_id != null && addressId === most_delivery_address_id) {
        return count === 1
          ? t('address.deliverySummaryRemainingOne')
          : t('address.deliverySummaryRemaining', { count: formatNumber(count) });
      }
      const dates = getAddressDates(addressId);
      const labels = buildAddressDeliverySummaryLabels(dates, all_particular_delivery_dates, locale);
      if (labels.length === 0) {
        return null;
      }
      return `${labels.join(', ')} ${t('address.deliverySummarySuffix')}`;
    },
    [
      upcoming_delivery_count_by_address,
      most_delivery_address_id,
      getAddressDates,
      all_particular_delivery_dates,
      locale,
      t,
    ],
  );

  const delivery_cta_label =
    assignment_mode === 'use_2' ? t('address.openDeliverySchedule') : use_redesigned_delivery_ux ? t('address.manageDeliveryDays') : t('address.setDeliveryDates');

  const handleEdit = useCallback(
    (address: any) => {
      navigation.navigate('AddressForm', { address });
    },
    [navigation],
  );

  const handleDelete = useCallback(
    (addressId: number) => {
      Alert.alert(t('address.deleteConfirmTitle'), t('address.deleteConfirmMessage'), [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('address.delete'),
          style: 'destructive',
          onPress: () => {
            deleteMutation.mutate(addressId, {
              onSuccess: () => refetch?.(),
              onError: () => Alert.alert(t('common.error'), t('address.deleteError')),
            });
          },
        },
      ]);
    },
    [deleteMutation, refetch, t],
  );

  const handleSetDefault = useCallback(
    (addressId: number) => {
      if (!user?.id) {
        return;
      }
      setDefaultMutation.mutate(
        { customerId: user.id, addressId },
        {
          onSuccess: () => refetch?.(),
          onError: () => Alert.alert(t('common.error'), t('address.setDefaultError')),
        },
      );
    },
    [user?.id, setDefaultMutation, refetch, t],
  );

  const handleAddNew = useCallback(() => {
    navigation.navigate('AddressForm', {});
  }, [navigation]);

  const handleOpenDeliveryDates = useCallback(
    (address: { id: number; name?: string }) => {
      if (assignment_mode === 'use_2') {
        navigation.navigate('DeliverySchedule', { address });
        return;
      }
      setDeliveryDatesAddress(address);
    },
    [assignment_mode, navigation],
  );

  const handleCloseDeliveryDatesModal = useCallback(
    (refreshNeeded: boolean) => {
      setDeliveryDatesAddress(null);
      if (refreshNeeded) {
        void (async () => {
          if (typeof user?.id === 'number') {
            await runInvalidationForMutation(query_client, {
              rule_id: 'address.deliverySchedule',
              customer_id: user.id,
            });
          }
          await refetch?.();
        })();
      }
    },
    [refetch, query_client, user?.id],
  );

  const content_top_offset = androidHeaderTopInset(insets.top) + HEADER_HEIGHT;
  const footer_total_height = footerTotalHeight(insets.bottom);
  const scroll_padding_bottom = footer_total_height + appTheme.spacing.lg;

  if (isLoading) {
    return (
      <StackScreenShell colors={themeColors}>
        <View style={styles.container}>
          <ScreenHeader title={t('address.title')} onBack={on_back_exit} scrollY={scrollY} />
          <View style={[styles.center, { paddingTop: content_top_offset }]}>
            <MainLoader size={48} color={themeColors.primary} />
          </View>
        </View>
      </StackScreenShell>
    );
  }

  if (error) {
    return (
      <StackScreenShell colors={themeColors}>
        <View style={styles.container}>
          <ScreenHeader title={t('address.title')} onBack={on_back_exit} scrollY={scrollY} />
          <View style={{ flex: 1, paddingTop: content_top_offset + 24 }}>
            <ErrorState
              title={t('address.loadError')}
              message={t('common.pleaseTryAgain')}
              onRetry={() => refetch?.()}
              error={error}
              issue_context={{
                page: t('address.title'),
                issue: 'Failed to load delivery addresses',
                kind: 'api',
                api_endpoint: 'addresses',
                http_method: 'GET',
              }}
            />
          </View>
        </View>
      </StackScreenShell>
    );
  }

  return (
    <StackScreenShell colors={themeColors}>
    <View style={styles.container}>
      <ScreenHeader
        title={t('address.deliveryAddresses')}
        onBack={on_back_exit}
        scrollY={scrollY}
        rightAction={
          <Clickable onPress={() => refetch?.()} style={styles.headerRefreshWrap} accessibilityLabel={t('common.refresh')} accessibilityRole="button">
            <View style={styles.headerRefreshInner}>
              <SvgIcon name="refresh" size={18} color={themeColors.text} />
            </View>
          </Clickable>
        }
      />

      <Animated.ScrollView
        style={styles.scroll}
        onScroll={onScroll}
        scrollEventThrottle={16}
        contentContainerStyle={[styles.scrollContent, { paddingTop: content_top_offset + appTheme.spacing.md, paddingBottom: scroll_padding_bottom }]}
        showsVerticalScrollIndicator={false}>
        {addresses.length === 0 ? (
          <View style={styles.emptyState}>
            <View style={styles.emptyIconWrap}>
              <SvgIcon name="map" size={48} color={themeColors.primary} />
            </View>
            <Text style={styles.emptyTitle}>{t('address.noAddresses')}</Text>
            <Text style={styles.emptySubtitle}>{t('address.emptySubtitle')}</Text>
          </View>
        ) : (
          addresses.map((address, index) => (
            <AddressCard
              key={address.id}
              address={address}
              index={index}
              styles={styles}
              themeColors={themeColors}
              onEdit={() => handleEdit(address)}
              onDelete={() => handleDelete(address.id)}
              onSetDefault={() => handleSetDefault(address.id)}
              onSetDeliveryDates={show_delivery_assignment ? () => handleOpenDeliveryDates({ id: address.id, name: address.name }) : undefined}
              showDeliveryDates={show_delivery_assignment}
              deliverySummary={show_delivery_assignment && use_redesigned_delivery_ux ? formatDeliverySummary(address.id) : null}
              deliveryCtaLabel={delivery_cta_label}
            />
          ))
        )}
      </Animated.ScrollView>

      <View
        style={[
          styles.pageFooter,
          {
            paddingBottom: FOOTER_VERTICAL_PAD + insets.bottom,
            minHeight: footer_total_height,
          },
        ]}>
        <View style={styles.pageFooterCtaWrap}>
          <Clickable
            onPress={handleAddNew}
            accessibilityLabel={t('address.addNew')}
            accessibilityRole="button"
            style={{ width: '100%' }}>
            <View style={outline_footer_cta.wrap}>
              <View style={[outline_footer_cta.inner, androidFlatSurfaceStyle()]}>
                <SvgIcon name="plus" size={22} color={themeColors.primary} />
                <Text style={outline_footer_cta.text}>{t('address.addNew')}</Text>
              </View>
            </View>
          </Clickable>
        </View>
      </View>

      {deliveryDatesAddress && user?.id != null && assignment_mode === 'disabled' ? (
        <AddressDeliveryDatesModal
          visible={true}
          onClose={handleCloseDeliveryDatesModal}
          address={deliveryDatesAddress}
          activeSubscriptions={Array.isArray(activeSubscriptions) ? activeSubscriptions : []}
          availableDates={Array.isArray(availableDates) ? availableDates : []}
          presetDates={Array.isArray(getAddressDates(deliveryDatesAddress.id)) ? getAddressDates(deliveryDatesAddress.id) : []}
          customerId={user.id}
        />
      ) : null}

      {deliveryDatesAddress && user?.id != null && assignment_mode === 'use_1' ? (
        <AddressDeliveryAssignmentSheet
          visible={true}
          onClose={handleCloseDeliveryDatesModal}
          address={deliveryDatesAddress}
          activeSubscriptions={Array.isArray(activeSubscriptions) ? activeSubscriptions : []}
          availableDates={Array.isArray(availableDates) ? availableDates : []}
          presetDates={Array.isArray(getAddressDates(deliveryDatesAddress.id)) ? getAddressDates(deliveryDatesAddress.id) : []}
          customerId={user.id}
        />
      ) : null}
    </View>
    </StackScreenShell>
  );
};

export default AddressListScreen;
